import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useMemo } from 'react';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import type { AlgoliaCardIndexes } from '@sorare/core/src/contexts/config';
import { useCardsOnSale } from '@sorare/core/src/hooks/search/useCardsOnSale';
import { CardsShow } from '@sorare/marketplace/src/components/market-home/CardsShow';
import { useMarketHomeFilters } from '@sorare/marketplace/src/hooks/useMarketHomeFilters';
import type { LeagueLaunch } from '@sorare/marketplace/src/lib/newSeason';
import type { CardResultsProps } from '@sorare/marketplace/src/searchCards/AdvancedCardSearch/types';

import type { TrendingCards_competition } from './__generated__/index.graphql';

type Props = {
  league: LeagueLaunch<
    TrendingCards_competition | undefined,
    object | undefined
  >;
  skip?: boolean;
} & CardResultsProps;

export const TrendingCards = ({ league, skip, ...props }: Props) => {
  const { marketHomeFilters } = useMarketHomeFilters();
  const { rarity } = marketHomeFilters;

  const sharedProps = useMemo(
    () => ({
      primaryMarket: true,
      rarities: [rarity],
      distinct: true,
      index: 'Cards On Sale Bids Count' as keyof AlgoliaCardIndexes,
    }),
    [rarity]
  );

  const propsCardsOnSale = useMemo(() => {
    if (league.sport === Sport.NBA) {
      return {
        ...sharedProps,
        addtionalAlgoliaFilters: league.extraFilters,
        skip,
      };
    }
    return {
      competitionSlugs:
        (league.competition?.slug && [league.competition?.slug]) || [],
      ...sharedProps,
      skip: skip || !league.competition?.slug,
    };
  }, [
    league.sport,
    league.extraFilters,
    skip,
    sharedProps,
    league.competition?.slug,
  ]);

  const { slugs, loading } = useCardsOnSale(propsCardsOnSale);

  return (
    <CardsShow
      hits={slugs.map(slug => ({ slug }))}
      loading={loading}
      {...props}
    />
  );
};

TrendingCards.fragments = {
  competition: gql`
    fragment TrendingCards_competition on Competition {
      slug
      name
    }
  ` as TypedDocumentNode<TrendingCards_competition>,
};
